import { useState } from "react";

import { isEmpty, keyBy } from "lodash-es";

import { FlowVersion } from "src/api/flowTypes";
import { getColorByHex } from "src/design-system/ColorIcon";
import { Card } from "src/performance/Card";
import { CardDropdown } from "src/performance/CardDropdown";
import { ErrorOverlay } from "src/performance/ErrorOverlay";
import { Legend, LegendItem } from "src/performance/Legend";
import { NoDataOverlay } from "src/performance/NoDataOverlay";
import { lineY, tooltip } from "src/performance/Plot/marks";
import { Plot } from "src/performance/Plot/Plot";
import { useLatency } from "src/performance/queries";
import { LatencyFunction, PerformanceFilters } from "src/performance/types";
import { displayLatency } from "src/performance/utils";
import { useFlowContext } from "src/router/routerContextHooks";
import { VersionColors } from "src/utils/getVersionsColors";

export const LatencyChart = ({
  filters,
  colors,
  displayedVersions,
  flowVersions,
}: {
  filters: PerformanceFilters;
  colors: VersionColors;
  displayedVersions?: string[];
  flowVersions?: FlowVersion[];
}) => {
  const [func, setFunc] = useState<string>("p50");
  const { flow, workspace } = useFlowContext();
  const versionsById = keyBy(flowVersions ?? flow.versions, "id");

  const latency = useLatency(workspace.base_url, {
    ...filters,
    func: func as LatencyFunction,
  });

  const latencyData =
    latency.data?.total.concat(latency.data?.by_version) ?? [];

  return (
    <Card>
      <Card.Header>
        Duration over time
        <CardDropdown
          elements={[
            { key: "p50", value: "P50" },
            { key: "p90", value: "P90" },
            { key: "p95", value: "P95" },
            { key: "max", value: "Max" },
          ]}
          selected={func}
          width="w-21"
          onSelect={setFunc}
        />
      </Card.Header>
      <Card.Body>
        {latency.isError && <ErrorOverlay onRetry={latency.refetch} />}
        {latency.isSuccess &&
          isEmpty(latency.data?.by_version) &&
          isEmpty(latency.data?.total) && <NoDataOverlay />}
        <Plot
          data={latencyData}
          isLoading={latency.isFetching}
          loadingStyle="line"
          marks={[
            lineY({
              x: "bin",
              y: "value",
              z: "version_id",
              stroke: (d) => (d.version_id ? colors[d.version_id] : "#9C9FAF"),
            }),
            tooltip({
              x: "bin",
              y: "value",
              title: (d) =>
                `${
                  versionsById[d.version_id]?.name ?? "All versions"
                }: ${displayLatency(d.value)}`,
              anchor: "top",
            }),
          ]}
          timeWindow={filters.timeWindow}
          y={{ tickFormat: displayLatency }}
        />
        <Legend
          items={[
            { color: "gray-400", name: "All versions", type: "circle" },
            ...((displayedVersions ?? filters.versions)?.map<LegendItem>(
              (versionId) => ({
                color: getColorByHex(colors[versionId]),
                name: versionsById[versionId]?.name ?? versionId,
                type: "circle",
              }),
            ) ?? []),
          ]}
        />
      </Card.Body>
    </Card>
  );
};
